import type { Iteration, Team, TeamMembership, TeamReferenceStory, TeamTag } from '@rhythmic/db/types'
import type {
  CreateIterationPayload,
  CreateTeamMembershipPayload,
  CreateTeamPayload,
  CreateTeamReferenceStoryPayload,
  TeamType,
  UpdateIterationPayload,
  UpdateTeamMembershipPayload,
  UpdateTeamReferenceStoryPayload,
} from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import type { ComputedRef, Ref } from 'vue'

import handleCreateNewIteration from './useTeamContext.createIteration'
import handleCreateTeam from './useTeamContext.createNewTeam'
import handleCreateHarmonyPlan from './useTeamContext.createPlan'
import handleCreateReferenceStory from './useTeamContext.createReferenceStory'
import handleCreateTeamMembership from './useTeamContext.createTeamMembership'
import type { CreateTeamTagPayload } from './useTeamContext.createTeamTag'
import handleCreateTeamTag from './useTeamContext.createTeamTag'
import type { CreateWorkitemTagPayload } from './useTeamContext.createWorkitemTag'
import handleCreateWorkitemTag from './useTeamContext.createWorkitemTag'
import handleDeleteTeamIteration from './useTeamContext.deleteIteration'
import handleDeleteHarmonyPlan from './useTeamContext.deletePlan'
import handleDeleteReferenceStory from './useTeamContext.deleteReferenceStory'
import handleDeleteTeamMembership from './useTeamContext.deleteTeamMembership'
import handleDeleteTeamTag from './useTeamContext.deleteTeamTag'
import type { DeleteWorkitemTagPayload } from './useTeamContext.deleteWorkitemTag'
import handleDeleteWorkitemTag from './useTeamContext.deleteWorkitemTag'
import { handleUpdateIteration } from './useTeamContext.updateIteration'
import handleUpdateReferenceStory from './useTeamContext.updateReferenceStory'
import type { UpdateTeamPayload } from './useTeamContext.updateTeam'
import handleUpdateTeam from './useTeamContext.updateTeam'
import handleUpdateTeamMembership from './useTeamContext.updateTeamMembership'
import type { UpdateTeamTagPayload } from './useTeamContext.updateTeamTag'
import handleUpdateTeamTag from './useTeamContext.updateTeamTag'
import type { UpdateWorkitemTagPayload } from './useTeamContext.updateWorkitemTag'
import handleUpdateWorkitemTag from './useTeamContext.updateWorkitemTag'

interface TeamContextLogger {
  debug: (message: string, meta?: Record<string, unknown>) => void
  info: (message: string, meta?: Record<string, unknown>) => void
  warn: (message: string, meta?: Record<string, unknown>) => void
  error: (message: string, meta?: Record<string, unknown>) => void
}

interface TeamContextMutatorsInit {
  logger: TeamContextLogger
  workspaceId: ComputedRef<string | undefined>
  workspaceSlug: Ref<string | undefined>
  teams: ComputedRef<TeamType[] | undefined>
  team: ComputedRef<TeamType | undefined>
}

export const mutators = {
  init: (deps: TeamContextMutatorsInit) => {
    function createNewTeam(payload: CreateTeamPayload): Transaction<Team> {
      if (!deps.workspaceId.value || !deps.workspaceSlug.value || !deps.teams.value) {
        const error = new Error('Workspace context is not ready')
        deps.logger.error('Unable to create team: workspace context is not ready', {
          workspaceId: deps.workspaceId.value,
        })
        throw error
      }

      return handleCreateTeam(payload, {
        workspaceId: deps.workspaceId.value,
        workspaceSlug: deps.workspaceSlug.value,
        teams: deps.teams.value,
      })
    }

    async function updateTeam(payload: UpdateTeamPayload) {
      if (!deps.workspaceId.value || !deps.workspaceSlug.value) {
        const error = new Error('Workspace context is not ready')
        deps.logger.error('Unable to update team: workspace context is not ready', {
          workspaceId: deps.workspaceId.value,
        })
        throw error
      }

      return handleUpdateTeam(payload, {
        workspaceId: deps.workspaceId.value,
        workspaceSlug: deps.workspaceSlug.value,
        teams: deps.teams.value,
      })
    }

    function createTeamMembership(payload: CreateTeamMembershipPayload): Transaction<TeamMembership> {
      const resolvedTeamId = payload.team_id ?? deps.team.value?.id
      if (!resolvedTeamId) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to create team membership: missing team identifier', {
          payloadTeamId: payload.team_id,
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      const normalizedPayload: CreateTeamMembershipPayload = {
        ...payload,
        team_id: resolvedTeamId,
      }

      return handleCreateTeamMembership(normalizedPayload, {
        teamId: resolvedTeamId,
      })
    }

    async function updateTeamMembership(
      userId: string,
      changes: UpdateTeamMembershipPayload,
      teamIdOverride?: string,
    ): Promise<TeamMembership | null> {
      const resolvedTeamId = teamIdOverride ?? deps.team.value?.id
      if (!resolvedTeamId) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to update team membership: missing team identifier', {
          teamIdOverride,
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      return handleUpdateTeamMembership({ userId, changes }, { teamId: resolvedTeamId })
    }

    function deleteTeamMembership(userId: string, teamIdOverride?: string): Transaction<TeamMembership> | undefined {
      const resolvedTeamId = teamIdOverride ?? deps.team.value?.id
      if (!resolvedTeamId) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to delete team membership: missing team identifier', {
          teamIdOverride,
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      return handleDeleteTeamMembership({ user_id: userId, team_id: resolvedTeamId }, { teamId: resolvedTeamId })
    }

    function createHarmonyPlan(payload: { title: string; teamId?: string | null }): Transaction {
      if (!deps.workspaceId.value || !deps.team.value?.id) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to create harmony plan: missing workspace or team context', {
          workspaceId: deps.workspaceId.value,
          teamId: deps.team.value?.id,
        })
        throw error
      }

      const resolvedTeamId = payload.teamId ?? deps.team.value.id

      return handleCreateHarmonyPlan(
        {
          title: payload.title,
          teamId: resolvedTeamId,
        },
        {
          workspaceId: deps.workspaceId.value,
          teamId: resolvedTeamId,
        },
      )
    }

    async function deleteHarmonyPlan(planId: string) {
      if (!deps.workspaceId.value || !deps.team.value?.id) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to delete harmony plan: missing workspace or team context', {
          workspaceId: deps.workspaceId.value,
          teamId: deps.team.value?.id,
        })
        throw error
      }

      await handleDeleteHarmonyPlan(planId, {
        teamId: deps.team.value.id,
        workspaceId: deps.workspaceId.value,
      })
    }

    function createReferenceStory(payload: CreateTeamReferenceStoryPayload): Transaction<TeamReferenceStory> {
      if (!deps.team.value?.id) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to create reference story: missing team identifier', {
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      return handleCreateReferenceStory(payload, {
        teamId: deps.team.value.id,
      })
    }

    function updateReferenceStory(id: string, changes: UpdateTeamReferenceStoryPayload): Transaction {
      if (!deps.team.value?.id) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to update reference story: missing team identifier', {
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      return handleUpdateReferenceStory({ id, changes }, { teamId: deps.team.value.id })
    }

    function deleteReferenceStory(id: string): Transaction {
      if (!deps.team.value?.id) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to delete reference story: missing team identifier', {
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      return handleDeleteReferenceStory({ id }, { teamId: deps.team.value.id })
    }

    function createIteration(payload: CreateIterationPayload): Transaction<Iteration> | undefined {
      if (!deps.team.value?.id) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to create iteration: missing team identifier', {
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      if (!deps.workspaceId.value) {
        const error = new Error('Workspace context is not ready')
        deps.logger.error('Unable to create iteration: missing workspace identifier', {
          workspaceId: deps.workspaceId.value,
        })
        throw error
      }

      return handleCreateNewIteration(payload, {
        team_id: deps.team.value.id,
        workspace_id: deps.workspaceId.value,
      })
    }

    function updateIteration(id: string, payload: UpdateIterationPayload): Transaction | undefined {
      if (!deps.team.value?.id) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to update iteration: missing team identifier', {
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      deps.logger.debug('Updating iteration---', {
        id,
        payload,
        teamId: deps.team.value.id,
      })
      return handleUpdateIteration(id, payload, { teamId: deps.team.value.id })
    }

    async function deleteIteration(id: string) {
      if (!deps.team.value?.id) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to delete iteration: missing team identifier', {
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      return handleDeleteTeamIteration(id, { teamId: deps.team.value.id })
    }

    function createTeamTag(payload: CreateTeamTagPayload): Transaction<TeamTag> {
      const resolvedTeamId = payload.team_id ?? deps.team.value?.id
      if (!resolvedTeamId) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to create team tag: missing team identifier', {
          payloadTeamId: payload.team_id,
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      return handleCreateTeamTag(payload, {
        teamId: resolvedTeamId,
        workspaceId: payload.workspace_id ?? deps.workspaceId.value ?? deps.team.value?.workspace_id ?? null,
      })
    }

    function updateTeamTag(tagId: string, payload: UpdateTeamTagPayload): Transaction<TeamTag> {
      const resolvedTeamId = deps.team.value?.id
      if (!resolvedTeamId) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to update team tag: missing team identifier', {
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      return handleUpdateTeamTag(tagId, payload, { teamId: resolvedTeamId })
    }

    function deleteTeamTag(tagId: string): Transaction<TeamTag> {
      const resolvedTeamId = deps.team.value?.id
      if (!resolvedTeamId) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to delete team tag: missing team identifier', {
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      return handleDeleteTeamTag(tagId, { teamId: resolvedTeamId })
    }

    function createWorkitemTag(payload: CreateWorkitemTagPayload): Transaction {
      const resolvedTeamId = deps.team.value?.id
      if (!resolvedTeamId) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to create workitem tag: missing team identifier', {
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      return handleCreateWorkitemTag(payload, {
        teamId: resolvedTeamId,
        workspaceId: payload.workspaceId ?? deps.workspaceId.value ?? deps.team.value?.workspace_id ?? null,
      })
    }

    function updateWorkitemTag(payload: UpdateWorkitemTagPayload): Transaction {
      const resolvedTeamId = deps.team.value?.id
      if (!resolvedTeamId) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to update workitem tag: missing team identifier', {
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      return handleUpdateWorkitemTag(payload, {
        teamId: resolvedTeamId,
        workspaceId: payload.workspaceId ?? deps.workspaceId.value ?? deps.team.value?.workspace_id ?? null,
      })
    }

    function deleteWorkitemTag(payload: DeleteWorkitemTagPayload): Transaction {
      const resolvedTeamId = deps.team.value?.id
      if (!resolvedTeamId) {
        const error = new Error('Team context is not ready')
        deps.logger.error('Unable to delete workitem tag: missing team identifier', {
          activeTeamId: deps.team.value?.id,
        })
        throw error
      }

      return handleDeleteWorkitemTag(payload, { teamId: resolvedTeamId })
    }

    return {
      createIteration,
      updateIteration,
      deleteIteration,
      createNewTeam,
      updateTeam,
      createTeamMembership,
      updateTeamMembership,
      deleteTeamMembership,
      createHarmonyPlan,
      deleteHarmonyPlan,
      createReferenceStory,
      updateReferenceStory,
      deleteReferenceStory,
      createTeamTag,
      updateTeamTag,
      deleteTeamTag,
      createWorkitemTag,
      updateWorkitemTag,
      deleteWorkitemTag,
    }
  },
}
